import { useEffect, useState } from "react";
import {
  BrowserRouter,
  Link,
  Navigate,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import { SessionProvider, useSession, isFirmSide } from "./lib/session";
import { originFor, surfaceForHost } from "./lib/surface";
import { api, type EngagementSummary } from "./lib/api";
import { useResource } from "./lib/useResource";
import { SignIn } from "./screens/SignIn";
import { Command } from "./screens/Command";
import { Workspace, Offers } from "./screens/Workspace";
import { Portal } from "./screens/Portal";
import { Files } from "./screens/Files";
import { Delivery } from "./screens/Delivery";
import { Invite } from "./screens/Invite";
import { People } from "./screens/People";
import { Engagements } from "./screens/Engagements";
import { Catch } from "./screens/Catch";
import { Firm } from "./screens/Firm";
import { Engagement } from "./screens/Engagement";
import { EngagementSetup } from "./screens/EngagementSetup";
import { Work as EngagementWork } from "./screens/Work";
import { Bills as EngagementBills } from "./screens/Bills";
import { AppShell, ScreenHead } from "./shell/AppShell";
import { Button, Empty, SkeletonRows } from "./components/primitives";
import { Bench } from "./screens/Bench";
import { Release } from "./screens/Release";

/* ---------------------------------------------------------------------------
   DIRECTION CONTRACT

   THESIS: The record is the interface. This platform's one irreducible property
   is that facts are written and never edited away — publication is an act with
   a name and a date, approval binds to one exact version, withdrawal is a
   written fact. So the primary surface is a register read in order, and it
   refuses the dashboard of stat tiles every consulting tool ships, which would
   hide exactly that.

   OWN-WORLD: Two inherited grounds — warm paper (42 22% 95%) for staff who live
   here all day, near-black (0 0% 5%) for the client who visits twice a month.
   Zero radius everywhere, hairlines at one device pixel, sand as the only
   accent. DM Sans tight for titles, Work Sans for everything that works,
   Cormorant once per client surface. Status is a printed mark, never a colour
   wash; a withdrawn fact is struck, not removed.

   STORY: A Lead opens this to learn what needs them, acts, and leaves. A
   consultant sees their own work and the scope that bounds it. A client
   understands where their engagement is and what is being asked of them,
   without contacting anyone.

   FIRST VIEWPORT: Left rail with the engagement pinned in it, because acting on
   the wrong engagement is the expensive mistake. Then the engagement name in
   the display face, status beside it, and immediately "Needs attention" — the
   first region, ruled not boxed. Figures hang in the right margin; the primary
   action sits on the row that needs it.

   FORM: The register. Candidate 5 of seven derived structures, assigned by
   concept-seed key b450d6cb (scope surface, mode operate), fused with the
   centre-rail reference-setting staging: rail of keys, short-measure block,
   wide annotation margin.
   --------------------------------------------------------------------------- */

export default function App() {
  // Redemption is intercepted above the session entirely. The person reading
  // it has no account yet by definition, so going through SessionProvider
  // would fire a /api/me that is guaranteed to 401 and would flash the
  // sign-in screen at someone who cannot sign in yet. Deliberately outside
  // the router too: the token lives in the URL fragment, which react-router
  // treats as part of the location and would strip on its first navigation.
  if (window.location.pathname === "/invite") return <Invite />;

  return (
    <BrowserRouter>
      <SessionProvider>
        <Router />
      </SessionProvider>
    </BrowserRouter>
  );
}

/**
 * Routing by role.
 *
 * The role decides which surface renders and nothing else. It is not an
 * authorisation decision: the server re-derives identity from the cookie on
 * every request and the database decides what comes back, so a tampered role
 * here changes what the interface offers and not one row of what it receives.
 */
function Router() {
  const { me, status } = useSession();

  if (status === "checking") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas px-6">
        <div className="w-full max-w-md">
          <SkeletonRows rows={3} />
        </div>
      </div>
    );
  }

  if (status === "signed-out" || !me) return <SignIn />;

  // Two hostnames, one process — people.arikco.com for the firm,
  // client.arikco.com for clients. This decides only whether the door someone
  // is standing at is the right one; it is not an authorisation check and must
  // never be mistaken for one. The server re-derives identity from the cookie
  // and the database decides what comes back, on both names alike. See
  // lib/surface.ts. Anywhere else — localhost, an IP, a preview — reports
  // "either" and the role decides exactly as it did before.
  const surface = surfaceForHost();
  const firmSide = isFirmSide(me.role);

  if (surface === "firm" && !firmSide) return <WrongDoor want="client" />;
  if (surface === "client" && firmSide) return <WrongDoor want="firm" />;

  // Clients never see the firm's surfaces, and the firm never sees the portal
  // chrome. Two different products wearing one identity.
  if (!firmSide) return <ClientRoutes />;

  return (
    <Routes>
      <Route path="/" element={me.role === "consultant" ? <Navigate to="/work" replace /> : <FirstEngagement />} />
      <Route path="/work" element={<Workspace />} />
      <Route path="/offers" element={<Offers />} />
      {/* The engagement itself: what it is called, who is on it, what it is
          made of. Command is a separate surface — actionable things, what
          needs you today — and lives one click away. Landing on Command
          answered a question nobody had asked yet. */}
      <Route path="/engagements/:id" element={<Engagement />} />
      {/* Setup is separate from the overview because they are for
          different people: the overview is what the engagement IS, which
          everyone on it can read, and this is how it is shaped, which only
          its Lead or a Partner can do. The screen checks mayManage itself. */}
      <Route path="/engagements/:id/setup" element={<EngagementSetup />} />
      {/* Desk was Command. The old name said nothing about what the screen
          does — it reports where the engagement stands and what is slipping.
          Review was Delivery, which was ambiguous between the work being
          delivered and the act of delivering; the screen is deliverables and
          the decisions on them. Both old paths redirect, because links to
          them exist in notifications and in people's history. */}
      <Route path="/engagements/:id/desk" element={<EngagementCommand />} />
      <Route path="/engagements/:id/notes" element={<EngagementDelivery />} />
      {/* Work is new: workstreams and the tasks inside them. Setup defines
          the shape; this is where the work happens. */}
      <Route path="/engagements/:id/work" element={<EngagementWork />} />
      {/* Scoped to the engagement in the address instead of resolving "the
          first engagement you can reach", which is what let the rail show
          one client's files while the list showed another's. */}
      <Route path="/engagements/:id/files" element={<EngagementFiles />} />
      {/* The publication moment. Specified under Motion in DESIGN.md since
          the first build and listed under known gaps ever since, because
          the surface it needed did not exist. */}
      <Route path="/engagements/:id/release" element={<Release />} />
      <Route path="/engagements/:id/bills" element={<EngagementBills />} />
      <Route
        path="/engagements/:id/command"
        element={<RenamedTo slug="desk" />}
      />
      <Route
        path="/engagements/:id/delivery"
        element={<RenamedTo slug="notes" />}
      />
      {/* Renamed twice in one day: Delivery -> Review -> Intake. The first
          rename was made on a wrong reading of the screen. Both old paths
          still land. */}
      <Route
        path="/engagements/:id/review"
        element={<RenamedTo slug="notes" />}
      />
      <Route
        path="/engagements/:id/intake"
        element={<RenamedTo slug="notes" />}
      />
      <Route path="/delivery" element={<FirstEngagementDelivery />} />
      <Route path="/files" element={<FirstEngagementFiles />} />
      {/* The index is readable by anyone firm-side and filtered by row-level
          security; the create form inside it is refused by the server for
          anyone below Principal Partner. */}
      {/* Super Admin only, enforced by the definer functions in 024 — a
          Partner who reaches this URL gets refusals, not a screen. */}
      <Route path="/firm" element={<Firm />} />
      <Route path="/engagements" element={<Engagements />} />
      {/* Rendered for anyone firm-side; the server refuses everyone but a
          Super Admin, so a Lead who reaches it sees refusals rather than a
          working form. The nav only offers it to a Super Admin. */}
      <Route path="/catch" element={<Catch />} />
      <Route path="/people" element={<People />} />
      {/* One route for the whole firm side. What comes back is filtered by
          consultant_profile_read, so a consultant reaching it sees their
          own entry and a form to write it, and a Partner sees the bench. */}
      <Route path="/bench" element={<Bench />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

/**
 * Which engagement?
 *
 * Now a real question answered by a real endpoint. This used to probe a
 * hardcoded fixture uuid against `/progress` and treat a 200 as proof the
 * engagement existed — which was a stub, said so, and then broke in the worst
 * possible way on the first deployment: `/progress` aggregates, and a sum over
 * no rows is 0 rather than an error, so the probe got a cheerful 200 for an
 * engagement that did not exist. The shell concluded the caller had one and
 * every subsequent call 404'd, so a brand-new platform rendered as a broken
 * screen instead of an empty one. Both halves are fixed: `/progress` refuses
 * an engagement it cannot see, and this asks the index.
 */
function useFirstEngagement() {
  const [state, setState] = useState<{ id: string | null; done: boolean }>({
    id: null,
    done: false,
  });

  useEffect(() => {
    let live = true;

    api
      .get<EngagementSummary[]>("/api/engagements")
      .then((list) => live && setState({ id: list[0]?.id ?? null, done: true }))
      .catch(() => live && setState({ id: null, done: true }));

    return () => {
      live = false;
    };
  }, []);

  return state;
}

function FirstEngagement() {
  const { id, done } = useFirstEngagement();

  if (!done) {
    return (
      <AppShell>
        <ScreenHead title="Desk" />
        <SkeletonRows rows={6} />
      </AppShell>
    );
  }

  if (!id) {
    return (
      <AppShell>
        <ScreenHead title="Desk" />
        <Empty
          title="You are not on an engagement yet"
          action={
            <Link
              to="/engagements"
              className="inline-flex min-h-11 items-center justify-center border border-ink bg-ink px-5 text-sm font-medium text-canvas transition-colors duration-200 hover:bg-ink/85"
            >
              Engagements
            </Link>
          }
        >
          A Partner adds you to an engagement, and it appears here with its
          record. If you are a Partner or Managing Partner you can start one
          yourself.
        </Empty>
      </AppShell>
    );
  }

  return <Command engagementId={id} />;
}

function FirstEngagementFiles() {
  const { id, done } = useFirstEngagement();

  if (!done) {
    return (
      <AppShell>
        <ScreenHead title="Files" />
        <SkeletonRows rows={6} />
      </AppShell>
    );
  }
  if (!id) {
    return (
      <AppShell>
        <ScreenHead title="Files" />
        <Empty title="No engagement selected">
          Choose an engagement before opening its files.
        </Empty>
      </AppShell>
    );
  }
  // Redirect rather than render. This route used to resolve "the
  // first engagement you can reach" and show it, which is what let
  // the rail show one client's work while the list showed another's.
  // Sending it to the named route means the address says which
  // engagement you are looking at, and old links still land.
  return <Navigate to={`/engagements/${id}/files`} replace />;
}

function FirstEngagementDelivery() {
  const { id, done } = useFirstEngagement();

  if (!done) {
    return (
      <AppShell>
        <ScreenHead title="Notes" />
        <SkeletonRows rows={6} />
      </AppShell>
    );
  }

  if (!id) {
    return (
      <AppShell>
        <ScreenHead title="Notes" />
        <Empty title="No engagement selected">
          Choose an engagement before opening its notes.
        </Empty>
      </AppShell>
    );
  }

  // Redirect rather than render. This route used to resolve "the
  // first engagement you can reach" and show it, which is what let
  // the rail show one client's work while the list showed another's.
  // Sending it to the named route means the address says which
  // engagement you are looking at, and old links still land.
  return <Navigate to={`/engagements/${id}/notes`} replace />;
}

/**
 * A screen that changed name keeps its old address working.
 *
 * Notifications, bookmarks and anything already in someone's history point
 * at `/command` and `/delivery`. Renaming the rail without this would turn
 * every one of those into a 404 — the rename would read as the platform
 * losing pages.
 */
function RenamedTo({ slug }: { slug: "desk" | "notes" }) {
  const { id } = useParams<{ id: string }>();
  if (!id) return <NotFound />;
  return <Navigate to={`/engagements/${id}/${slug}`} replace />;
}

function EngagementDelivery() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <NotFound />;
  return <Delivery engagementId={id} />;
}

function EngagementFiles() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <NotFound />;
  return <Files engagementId={id} />;
}

function EngagementCommand() {
  // Was reading path segment [1] by hand, which happened to work while the
  // route was /engagements/:id and silently would not now that Command sits
  // at /engagements/:id/command. useParams asks the router rather than the
  // string.
  const { id } = useParams<{ id: string }>();
  if (!id) return <NotFound />;
  return <Command engagementId={id} />;
}

/**
 * The client's side of the building.
 *
 * It used to render `useFirstEngagement()` — literally the first row of
 * the list — with no way to reach any other. A client of a firm with two
 * matters open could see one of them, permanently, and nothing on screen
 * suggested the other existed. That is not a small omission for a client
 * portal: the engagements are separate grants, deliberately, and the
 * whole point of opening one is that it does not open the rest.
 *
 * Now: one engagement goes straight through as before, and more than one
 * gets a quiet switcher. The address carries the id so a bookmark lands
 * where it was left.
 */
function ClientRoutes() {
  const { resource } = useResource<EngagementSummary[]>(
    () => api.get<EngagementSummary[]>("/api/engagements"),
    [],
  );

  if (resource.state === "loading") {
    return (
      <div data-world="ink" className="flex min-h-screen items-center justify-center bg-canvas px-6">
        <div className="w-full max-w-md">
          <SkeletonRows rows={3} />
        </div>
      </div>
    );
  }

  const open = resource.state === "ready" ? resource.data : [];

  if (open.length === 0) {
    return (
      <div data-world="ink" className="flex min-h-screen flex-col items-center justify-center bg-canvas px-6 text-center">
        <p className="label">Arikekpar &amp; Company</p>
        <h1 className="mt-4 max-w-[30ch] font-editorial text-2xl text-ink">
          Your workspace is being prepared
        </h1>
        <p className="mt-3 max-w-[44ch] text-base text-secondary">
          Your engagement team will let you know the moment it is ready. There is
          nothing you need to do.
        </p>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to={`/e/${open[0]!.id}`} replace />} />
      {/* `/*` because the portal owns its own pages now — Actions, Progress,
          Deliverables, Files, Invoices — and renders a nested <Routes> for
          them. Without the splat, `/e/<id>/invoices` would fall through to
          the catch-all below and bounce the client to the overview. */}
      <Route
        path="/e/:id/*"
        element={<ClientPortalAt engagements={open} />}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** One engagement, with a way across to the others when there are any. */
function ClientPortalAt({ engagements }: { engagements: EngagementSummary[] }) {
  const { id } = useParams<{ id: string }>();
  const here = engagements.find((e) => e.id === id);

  // An id that is not theirs is not an error to explain — it is simply
  // not one of their engagements, and the first one is.
  if (!here) return <Navigate to={`/e/${engagements[0]!.id}`} replace />;

  return (
    <>
      {engagements.length > 1 && (
        <nav
          aria-label="Your engagements"
          data-world="ink"
          className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-line bg-canvas px-5 py-3 text-sm sm:px-8"
        >
          <span className="label">Your engagements</span>
          {engagements.map((e) => (
            <Link
              key={e.id}
              to={`/e/${e.id}`}
              aria-current={e.id === here.id ? "page" : undefined}
              className={
                "border-b-2 pb-0.5 transition-colors duration-150 " +
                (e.id === here.id
                  ? "border-ink text-ink"
                  : "border-transparent text-secondary hover:text-ink")
              }
            >
              {e.name}
            </Link>
          ))}
        </nav>
      )}
      <Portal engagementId={here.id} />
    </>
  );
}

/**
 * Signed in, but at the other address.
 *
 * Deliberately not a redirect. The session cookie is `SameSite=Strict` and so
 * is scoped to the hostname it was issued for — bouncing someone to the other
 * name would land them there signed out, having just successfully signed in,
 * which reads as the system losing their session rather than as them using
 * the wrong door. Saying so plainly, once, is kinder than a redirect that
 * looks like a bug.
 *
 * The sign-out is offered because there *is* a live session on this hostname
 * now, and leaving one behind on a machine someone else may use is untidy at
 * best. `want` is the surface the person belongs to, so it also decides which
 * of the two grounds this is drawn on.
 */
function WrongDoor({ want }: { want: "firm" | "client" }) {
  const { signOut } = useSession();
  const target = originFor(want);
  const pretty = target.replace(/^https?:\/\//, "");

  return (
    <div
      data-world={want === "client" ? "ink" : undefined}
      className="flex min-h-screen flex-col items-center justify-center bg-canvas px-6 text-center"
    >
      <p className="label">Arikekpar &amp; Company</p>

      <h1 className="mt-4 max-w-[32ch] font-editorial text-2xl text-ink">
        {want === "client"
          ? "Your portal is at a different address"
          : "This address is the client portal"}
      </h1>

      <p className="mt-3 max-w-[46ch] text-base text-secondary">
        {want === "client"
          ? "This address is for the firm's own team. Your engagement lives at the address below."
          : "Your workspace is at the address below. This one shows clients their own engagement and nothing else."}
      </p>

      <a
        href={target}
        className="mt-6 inline-flex min-h-11 items-center justify-center border border-ink bg-ink px-5 text-sm font-medium text-canvas transition-colors duration-200 hover:bg-ink/85"
      >
        Go to {pretty}
      </a>

      <p className="mt-4 max-w-[46ch] text-sm text-secondary">
        You will be asked to sign in again there. For security, each address
        requires a separate sign-in.
      </p>

      <Button tone="quiet" className="mt-6" onClick={() => void signOut()}>
        Sign out of this address
      </Button>
    </div>
  );
}

function NotFound() {
  return (
    <AppShell>
      <ScreenHead title="Not found" />
      <Empty title="There is nothing at this address">
        The link may be out of date, or it may point at something that is not
        yours. Either way there is nothing to show.
      </Empty>
    </AppShell>
  );
}
